import { Group, Person, Security } from "@mui/icons-material";
import React from "react";

import DescriptiveTooltip from "../../../../components/DescriptiveTooltip";
import defaultFieldRenderer from "../../../../data/defaultFieldRenderer";
import Date from "../../../../data/fieldRenderers/Date";
import ID from "../../../../data/fieldRenderers/ID";
import Resource from "../../../../data/fieldRenderers/Resource";
import type { FieldRenderers } from "../../../../data/types";
import type { DataType } from "./fields";
import type { RuleSource, RuleSourceKind } from "./types";

const SOURCE_KIND_LABELS: Record<RuleSourceKind, string> = {
  direct: "Direct grant",
  role: "Role",
  team: "Team",
  unknown: "Unknown",
};

const SOURCE_KIND_ICONS: Record<RuleSourceKind, React.ElementType | null> = {
  direct: Person,
  role: Security,
  team: Group,
  unknown: null,
};

function getSourceLabel(source: RuleSource): string {
  if (!source) return "";
  if (source.kind === "direct") return "Direct grant";
  return source.id ?? source.kind;
}

const SourceRenderer = React.memo(function SourceRenderer({
  source,
  defaultNode,
}: {
  source: RuleSource;
  defaultNode: React.ReactElement;
}) {
  const Icon = SOURCE_KIND_ICONS[source.kind];
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {Icon && (
        <DescriptiveTooltip title={SOURCE_KIND_LABELS[source.kind]}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              flexShrink: 0,
              marginRight: 6,
            }}
          >
            <Icon fontSize="small" color="action" />
          </span>
        </DescriptiveTooltip>
      )}
      <div style={{ minWidth: 0 }}>{defaultNode}</div>
    </div>
  );
});

const fieldRenderers: FieldRenderers<DataType> = {
  ID,
  Resource,
  Date,
  Source: (fieldValue) => {
    const source = fieldValue as RuleSource;
    const label = getSourceLabel(source);
    const { formattedText, node: defaultNode } = defaultFieldRenderer(label);
    return {
      formattedText,
      node: <SourceRenderer source={source} defaultNode={defaultNode} />,
    };
  },
  Boolean: (fieldValue) => ({
    formattedText: fieldValue ? "Yes" : "No",
  }),
};

export default fieldRenderers;
